import { BUNDLE_FAMILY_NAVIGATION_STATE } from 'constants/bundles'
import moment from 'moment'
import { useCallback, useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { useApp } from 'store/steps'
import {
    setFamilyPackageVisibility,
    setFamilyPackageSelection,
    setFamilyPackageSelectionInterface,
} from 'store/ticket-slice'
import { useFindTicketProductBundlesQuery } from 'store/api-slice/hooks'
import { usePromotions } from 'components/shared/Promotions/utils/usePromotions'
import { FAMILY_PACKAGE_PERSON_COUNT } from 'components/global/Family/constants'
// import { apiController } from 'store/api-slice/index'

export const useFamilyCheckbox = () => {
    const dispatch = useAppDispatch()
    const location = useLocation()
    const navigate = useNavigate()
    const { language } = useApp()
    const { shouldPromoDisableTicketsBundles } = usePromotions()

    const { dates, people, familyPackage } = useAppSelector(
        (state) => state.ticket
    )
    const { ticketsBundles } = useAppSelector((state) => state.cart.items)

    const { isSelected, isVisible } = familyPackage
    const stateFromNavigation = location.state as { from: string }
    const startDate = dates?.length
        ? dates[0]
        : moment().add(1, 'd').format('YYYY-MM-DD')
    const endDate = dates?.length
        ? dates[dates.length - 1]
        : moment().add(1, 'd').format('YYYY-MM-DD')

    const today = moment().format('YYYY-MM-DD')

    const isDataFetchingDisabled =
        (!isVisible && !dates.length) ||
        (!isVisible && people < 3) ||
        shouldPromoDisableTicketsBundles

    const { data, isFetching: isDataFetching } =
        useFindTicketProductBundlesQuery(
            {
                _locale: language,
                startDate,
                endDate,
                cartType: 'creator',
            },
            {
                skip: isDataFetchingDisabled,
            }
        )

    const reducedData = useMemo(() => {
        const productBundles = data?.productBundles

        if (!productBundles || !productBundles.length) return undefined

        return productBundles?.reduce(
            (accumulator, currentValue) => {
                const {
                    name: accName,
                    unitPrice: accUnitPrice,
                    descriptions: accDescriptions,
                    dates: accDates,
                    status: accStatus,
                } = accumulator

                const {
                    name: currentName,
                    unitPrice: currentUnitPrice,
                    descriptions: currentDescriptions,
                    dates: currentDates,
                    status: currentStatus,
                } = currentValue

                // get name and descriptions from the first item
                const name = accName || currentName
                const descriptions = accDescriptions?.length
                    ? [...accDescriptions]
                    : [...(currentDescriptions || [])]
                // sum and combine remaining values from all the items
                const unitPrice =
                    (accUnitPrice || 0) +
                    (currentUnitPrice || 0) * (currentDates?.length || 0)
                const dates = [...(accDates || []), ...(currentDates || [])]
                const status = {
                    active: accStatus?.active && currentStatus?.active,
                }

                return { name, unitPrice, descriptions, dates, status }
            },
            {
                name: '',
                unitPrice: 0,
                descriptions: [],
                dates: [],
                status: { active: true },
            }
        )
    }, [data?.productBundles])

    const isDataComplete =
        !!reducedData?.status?.active &&
        reducedData?.dates?.length === dates.length

    useEffect(() => {
        if (!dates.length || dates.includes(today)) {
            dispatch(setFamilyPackageVisibility(false))
            dispatch(setFamilyPackageSelection({ isSelected: false }))
        }
    }, [dates])

    useEffect(() => {
        // <--
        // logic for limiting family package visibility
        // based on number of selected people
        const visibilityPeopleLimit = FAMILY_PACKAGE_PERSON_COUNT - 1
        const areTicketsBundlesInCart = !!ticketsBundles.length

        if (
            people < visibilityPeopleLimit &&
            isVisible &&
            !areTicketsBundlesInCart
        ) {
            dispatch(setFamilyPackageVisibility(false))
            return
        }

        if (people >= visibilityPeopleLimit && !isVisible && !!dates.length)
            dispatch(setFamilyPackageVisibility(true))
        // -->
    }, [people, isVisible, ticketsBundles, dates])

    useEffect(() => {
        if (isDataFetching) return
        if (!data) dispatch(setFamilyPackageVisibility(false))
    }, [isDataFetching, data])

    useEffect(() => {
        if (
            stateFromNavigation &&
            stateFromNavigation.from === BUNDLE_FAMILY_NAVIGATION_STATE &&
            !shouldPromoDisableTicketsBundles
        ) {
            dispatch(setFamilyPackageVisibility(true))
            dispatch(setFamilyPackageSelection({ isSelected: true }))
            navigate('/', { state: null })
            return
        }

        return () => cleanState()
    }, [])

    const cleanState = () => {
        // TODO: check possibility of using AbortController on single queries,
        // ...it's simple with mutations
        // apiController.abort()
        dispatch(setFamilyPackageVisibility(false))
        dispatch(setFamilyPackageSelection({ isSelected: false }))
    }

    const handleCheckboxVisibility = (isVisible: boolean) =>
        dispatch(setFamilyPackageVisibility(isVisible))

    const handleCheckboxSelection = (
        props: setFamilyPackageSelectionInterface
    ) => dispatch(setFamilyPackageSelection(props))

    const handleTicketsVisibility = useCallback(
        (increaseFn: () => void, clearFn?: () => void) => {
            if (clearFn) {
                clearFn()
                return
            }
            if (!isSelected) {
                dispatch(setFamilyPackageSelection({ isSelected: true }))
                increaseFn()
                return
            }
            dispatch(setFamilyPackageSelection({ isSelected: true }))
            return
        },
        [isSelected]
    )

    const isDisabled = useMemo(() => {
        if (
            stateFromNavigation &&
            stateFromNavigation.from === BUNDLE_FAMILY_NAVIGATION_STATE
        )
            return false

        return dates.includes(today) || !dates.length
    }, [dates])

    return {
        data: data?.productBundles,
        reducedData,
        isDisabled,
        isVisible,
        isSelected,
        isDataFetching,
        isDataComplete,
        handleCheckboxVisibility,
        handleCheckboxSelection,
        handleTicketsVisibility,
    }
}
